import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';

import cx from 'classnames';
import { Button } from 'components';
import { Icon } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { ContentRendererContext } from 'lane-shared/contexts';
import { hasPermission } from 'lane-shared/helpers';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import useFlag from 'lane-shared/hooks/useFlag';
import useUserDataContext from 'lane-shared/hooks/useUserDataContext';
import { FeatureFlag } from 'constants-flags';
import { FeatureNameEnum } from 'constants-content';
import { PERMISSION_KEYS } from 'constants-permissions';
import { useContentEditorContext } from 'contexts/ContentEditorContext';
import type {
  FeatureInstance,
  VisitorCustomFieldConfig,
  VisitorManagementFeatureProperties,
} from 'application-types';

import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { convertToUUID } from 'uuid-encoding';

import { useVisitorCustomFieldConfigs } from 'domains/visitorManagement/hooks/useVisitorCustomFieldConfigs';

import { CustomFieldsModal } from './CustomFieldsModal';
import { CustomFieldRenderer } from './CustomFieldRenderer';
import type { CustomFieldsSectionProps } from './customField.types';

import styles from './CustomFieldsSection.scss';

function isVisitorManagementFeature(
  cf: FeatureInstance
): cf is FeatureInstance<VisitorManagementFeatureProperties> {
  return cf.type === FeatureNameEnum.VisitorManagement;
}

export function CustomFieldsSection({
  className,
  isPreview = false,
  hideReadOnlyCustomFields = false,
  onConfigsLoaded,
}: CustomFieldsSectionProps) {
  const { t } = useTranslation();
  const { editMode, content } = useContext(ContentRendererContext);
  const { onContentUpdated } = useContentEditorContext();
  const userDataContext = useUserDataContext();
  const channelAdminContext = useChannelAdminContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addedFields, setAddedFields] = useState<VisitorCustomFieldConfig[]>(
    []
  );
  const initializedRef = useRef(false);
  /** When the user saves an empty selection, `content` may lag behind local state; skip restoration until persisted IDs reflect empty. */
  const awaitingPersistedEmptyAfterClearRef = useRef(false);
  const customFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );

  const user = userDataContext?.user;
  const channel = channelAdminContext?.channel;

  const isAdmin =
    user?.isSuperUser ||
    hasPermission(
      user?.roles || [],
      [PERMISSION_KEYS.PERMISSION_ADMIN],
      channel?._id
    );

  const rawOrgId = channel?._id;
  const organizationId = rawOrgId ? convertToUUID(rawOrgId) : '';

  const vmFeature = useMemo(() => {
    const cf = content?.features?.find(isVisitorManagementFeature);

    return cf?.feature;
  }, [content?.features]);

  const persistedCustomFieldIds = vmFeature?.customFieldIds;

  const { data: enabledCustomFields, loading, called, error, fetch: fetchCustomFields } =
    useVisitorCustomFieldConfigs();

  const lastNotifiedConfigsRef = useRef<typeof enabledCustomFields | null>(null);

  const customFieldConfigs = vmFeature?.customFieldConfigs;

  useEffect(() => {
    if (!customFieldsEnabled || initializedRef.current) return;

    if (customFieldConfigs?.length) {
      const enabledCustomFields = customFieldConfigs.filter(f => f.isEnabled);

      if (enabledCustomFields.length > 0) setAddedFields(enabledCustomFields );

      onConfigsLoaded?.(customFieldConfigs);
      initializedRef.current = true;
      
      return;
    }

    if (persistedCustomFieldIds?.length && organizationId) {
      initializedRef.current = true;
    }
  }, [
    customFieldsEnabled,
    customFieldConfigs,
    persistedCustomFieldIds,
    organizationId,
    onConfigsLoaded,
  ]);

  useEffect(() => {
    if (called && !loading && enabledCustomFields !== lastNotifiedConfigsRef.current) {
      lastNotifiedConfigsRef.current = enabledCustomFields;
      onConfigsLoaded?.(enabledCustomFields);
    }
  }, [called, loading, enabledCustomFields, onConfigsLoaded]);

  useEffect(() => {
    if (!persistedCustomFieldIds?.length) {
      awaitingPersistedEmptyAfterClearRef.current = false;
    }
  }, [persistedCustomFieldIds]);

  useEffect(() => {
    if (
      customFieldsEnabled &&
      enabledCustomFields.length > 0 &&
      persistedCustomFieldIds?.length &&
      addedFields.length === 0 &&
      !awaitingPersistedEmptyAfterClearRef.current
    ) {
      const persistedSet = new Set(persistedCustomFieldIds);
      const restored = enabledCustomFields.filter(f => persistedSet.has(f.id) && f.isEnabled);

      if (restored.length > 0) {
        setAddedFields(restored);
      }
    }
  }, [customFieldsEnabled, enabledCustomFields, persistedCustomFieldIds, addedFields.length]);

  const updateContentFeature = useCallback(
    (fieldIds: string[], fieldConfigs: VisitorCustomFieldConfig[]) => {
      if (!onContentUpdated || !content?.features) return;

      const updatedFeatures = content.features.map(
        (cf: FeatureInstance): FeatureInstance => {
          if (isVisitorManagementFeature(cf)) {
            return {
              ...cf,
              feature: {
                ...cf.feature,
                customFieldIds: fieldIds,
                customFieldConfigs: fieldConfigs,
              },
            };
          }

          return cf;
        }
      );

      onContentUpdated({ features: updatedFeatures });
    },
    [onContentUpdated, content?.features]
  );

  const handleOpenModal = useCallback(() => {
    if (!called || error) {
      fetchCustomFields(organizationId);
    }

    setIsModalOpen(true);
  }, [called, error, fetchCustomFields, organizationId]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSaveFields = useCallback(
    (fields: VisitorCustomFieldConfig[]) => {
      awaitingPersistedEmptyAfterClearRef.current = fields.length === 0;
      setAddedFields(fields);
      setIsModalOpen(false);
      updateContentFeature(fields.map(f => f.id), fields);
    },
    [updateContentFeature]
  );

  const alreadyAddedFieldIds = useMemo(
    () => addedFields.map(f => f.id),
    [addedFields]
  );

  if (!customFieldsEnabled) {
    return null;
  }

  if (!editMode || !isAdmin || isPreview) {
    if (hideReadOnlyCustomFields) {
      return null;
    }

    if (addedFields.length === 0) return null;

    return (
      <div
        className={styles.addedFieldsContainer}
        data-test="addedFieldsContainer"
      >
        <h3 className={styles.addedFieldsHeading}>
          {t(
            'web.admin.content.features.visitor.management.general.customFields.addedFieldsTitle'
          )}
        </h3>
        {addedFields.map(field => (
          <CustomFieldRenderer key={field.id} field={field} />
        ))}
      </div>
    );
  }

  return (
    <>
      {!hideReadOnlyCustomFields && addedFields.length > 0 && (
        <div
          className={styles.addedFieldsContainer}
          data-test="addedFieldsContainer"
        >
          <h3 className={styles.addedFieldsHeading}>
            {t(
              'web.admin.content.features.visitor.management.general.customFields.addedFieldsTitle'
            )}
          </h3>
          {addedFields.map(field => (
            <CustomFieldRenderer key={field.id} field={field} />
          ))}
        </div>
      )}

      <div
        className={cx(styles.customFieldsSection, className)}
        data-test="customFieldsSection"
      >
        <Button
          variant="outlined"
          className={styles.addCustomFieldsButton}
          onClick={handleOpenModal}
          testId="addCustomFieldsButton"
          startIcon={
            <Icon
              name="plus"
              set={ICON_SET_FONTAWESOME}
              testId="addCustomFieldsIcon"
            />
          }
        >
          {t(
            'web.admin.content.features.visitor.management.general.customFields.addButton'
          )}
        </Button>
      </div>

      {isModalOpen && (
        <CustomFieldsModal
          isOpen={isModalOpen}
          customFields={enabledCustomFields}
          loading={loading}
          error={error}
          onClose={handleCloseModal}
          onSave={handleSaveFields}
          alreadyAddedFieldIds={alreadyAddedFieldIds}
          hasFetched={called || enabledCustomFields.length > 0}
        />
      )}
    </>
  );
}
